import { debounce, requestIdleCallback, viewTransition, isMobileBreakpoint } from '@theme/utilities';
import { Component } from '@theme/component';

const OFFSET = 40;

/**
 * A custom element that manages a floating panel.
 */
export class FloatingPanelComponent extends Component {
  get #shouldFullscreenOnMobile() {
    return this.dataset.fullscreenMobile === 'true';
  }

  #updatePosition = async () => {
    // Wait for any view transitions to finish
    if (viewTransition.current) await viewTransition.current;

    // Clear any previous correction before measuring, otherwise a later
    // call (e.g. on re-open) measures its own prior offset instead of the
    // true CSS-default position, compounding into a wrong result.
    this.style.removeProperty('left');
    this.style.removeProperty('width');

    // Full-screen mobile panels are sized entirely by CSS
    // (position: fixed; inset: 0) - the left/width math below is only for
    // anchoring the desktop dropdown under its own trigger position.
    if (this.#shouldFullscreenOnMobile && isMobileBreakpoint()) {
      this.#mutationObserver.takeRecords();
      return;
    }

    const rect = this.getBoundingClientRect();
    const viewportWidth = window.innerWidth;

    // Full-viewport-width panels (e.g. the Color filter's swatch grid) anchor
    // to the true viewport edges directly, since their containing block is
    // the individual trigger's own position - not necessarily centered - so
    // percentage/margin-based CSS full-bleed tricks land in the wrong place
    // depending on which filter they're attached to. Skip the normal
    // overflow-nudge logic below; it isn't needed once left/width are exact.
    if (this.dataset.fullViewportWidth === 'true') {
      this.style.left = `${-rect.left}px`;
      this.style.width = `${viewportWidth}px`;
      this.#mutationObserver.takeRecords();
      return;
    }

    this.style.top = OFFSET + 'px';

    if (rect.right > viewportWidth) {
      const overflowAmount = rect.right - viewportWidth + OFFSET;
      this.style.left = `-${overflowAmount}px`;
    }

    if (rect.left < 0) {
      const overflowAmount = Math.abs(rect.left) + OFFSET;
      this.style.left = `${overflowAmount}px`;
    }

    this.#mutationObserver.takeRecords();
  };

  #mutationObserver = new MutationObserver(this.#updatePosition);

  #resizeListener = debounce(() => {
    const parent = this.closest('details');
    const closeOnResize = this.dataset.closeOnResize === 'true';
    if (parent instanceof HTMLDetailsElement && closeOnResize) {
      parent.open = false;
      parent.removeAttribute('open');
      this.#updatePosition();
      return;
    }

    // Full-viewport-width panels compute an exact left/width for the
    // viewport size at the moment they open - if the viewport changes size
    // while one is still open (window resize, opening devtools, rotating a
    // device), those values go stale and the panel drifts out of alignment.
    if (this.dataset.fullViewportWidth === 'true' && parent instanceof HTMLDetailsElement && parent.open) {
      this.#updatePosition();
    }
  }, 100);

  /** @type {HTMLDetailsElement|null} */
  #parentDetails = null;

  #handleToggle = () => {
    if (!this.#parentDetails?.open) return;
    // Measuring immediately on 'toggle' can catch layout mid-transition
    // (e.g. while a pending view transition is still resolving), producing
    // a wrong rect. Wait two frames so the browser has fully painted the
    // open state before measuring.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.#updatePosition());
    });
  };

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('resize', this.#resizeListener);

    // A closed <details>'s non-summary children aren't rendered, so a
    // measurement taken at connect time (before the user ever opens it)
    // reflects a zero/stale rect. Re-measure fresh each time it actually
    // opens - needed for data-full-viewport-width, where the exact trigger
    // position must be known to anchor correctly to the viewport edges.
    const parent = this.closest('details');
    if (parent instanceof HTMLDetailsElement) {
      this.#parentDetails = parent;
      parent.addEventListener('toggle', this.#handleToggle);
    }

    requestIdleCallback(() => {
      this.#updatePosition();
      this.#mutationObserver.observe(this, { attributes: true });
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.#parentDetails?.removeEventListener('toggle', this.#handleToggle);
    this.#mutationObserver.disconnect();
  }
}

if (!customElements.get('floating-panel-component')) {
  customElements.define('floating-panel-component', FloatingPanelComponent);
}
